import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { InternalLayout } from "@/components/InternalLayout";
import { StatusPill } from "@/components/StatusPill";
import { fmtDate, RUN_STATUS, STATUS_LABELS } from "@/lib/constants";
import { Plus, Hammer, Lock, Trash2, X } from "lucide-react";
import { toast } from "sonner";

export default function Builds() {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [project, setProject] = useState(null);
  const [members, setMembers] = useState([]);
  const [builds, setBuilds] = useState([]);
  const [findings, setFindings] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: "", prompt_set_id: "", prompt_version: "", owner_id: "", purpose: "", notes: "" });
  const [users, setUsers] = useState([]);

  useEffect(() => {
    api.get("/projects").then((r) => {
      setProjects(r.data);
      const url = new URL(window.location.href);
      setProjectId(url.searchParams.get("project") || r.data[0]?.project_id || "");
    });
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  }, []);

  const load = async (id) => {
    if (!id) return;
    const [b, f, p, prj] = await Promise.all([
      api.get(`/projects/${id}/builds`),
      api.get(`/projects/${id}/findings`),
      api.get(`/projects/${id}/prompts`),
      api.get(`/projects/${id}`),
    ]);
    setBuilds(b.data);
    setFindings(f.data);
    setPrompts(p.data);
    setProject(prj.data);
    setMembers(prj.data.members || []);
  };
  useEffect(() => { load(projectId); }, [projectId]);

  // Nur abgeschlossene Set-Versionen sind buildbar
  const buildableVersions = useMemo(() => {
    const out = [];
    prompts.forEach((s) => {
      (s.versions || []).forEach((v) => {
        if (v.status === "abgeschlossen") out.push({ set_id: s.set_id, set_name: s.name, version: v.version, discovery: v.discovery_pack_version });
      });
    });
    return out;
  }, [prompts]);

  const onCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/projects/${projectId}/builds`, {
        name: form.name,
        prompt_set_id: form.prompt_set_id,
        prompt_version: Number(form.prompt_version),
        owner_id: form.owner_id,
        purpose: form.purpose,
        notes: form.notes,
        artifacts: [],
      });
      toast.success("Build angelegt");
      setShowNew(false);
      setForm({ name: "", prompt_set_id: "", prompt_version: "", owner_id: "", purpose: "", notes: "" });
      load(projectId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Anlegen fehlgeschlagen");
    }
  };

  const setStatus = async (run_id, status) => {
    try {
      await api.patch(`/projects/${projectId}/builds/${run_id}`, { status });
      toast.success(`Status: ${STATUS_LABELS[status]}`);
      load(projectId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Statuswechsel fehlgeschlagen");
    }
  };

  const updateField = async (run_id, field, value) => {
    try {
      await api.patch(`/projects/${projectId}/builds/${run_id}`, { [field]: value });
      load(projectId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update fehlgeschlagen");
    }
  };

  const addArtifact = async (build) => {
    const url = window.prompt("Artefakt-URL:");
    if (!url) return;
    const type = window.prompt("Typ (preview, repo, asset, bericht):", "preview") || "preview";
    const list = [...(build.artifacts || []), { type, url, label: "" }];
    updateField(build.run_id, "artifacts", list);
  };

  const removeArtifact = async (build, idx) => {
    const list = (build.artifacts || []).filter((_, i) => i !== idx);
    updateField(build.run_id, "artifacts", list);
  };

  const addFinding = async (run_id) => {
    const title = window.prompt("Finding-Titel:");
    if (!title) return;
    const severity = window.prompt("Severity (info|low|medium|high|critical):", "medium") || "medium";
    try {
      await api.post(`/projects/${projectId}/findings?run_type=build&run_id=${run_id}`, { title, severity, status: "offen" });
      toast.success("Finding angelegt");
      load(projectId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Finding fehlgeschlagen");
    }
  };

  const userName = (uid) => users.find((u) => u.user_id === uid)?.name || uid;

  return (
    <InternalLayout>
      <div className="mb-6 flex items-end justify-between">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-500 mb-2">Phase 3</div>
          <h1 className="font-display text-3xl md:text-4xl text-neutral-900 flex items-center gap-3">Builds <Hammer className="w-6 h-6 text-[#183153]" /></h1>
          <p className="text-sm text-neutral-600 mt-2">Build Runs sind dokumentarisch. Pflichtbindung an eine abgeschlossene Prompt-Set-Version. Abgeschlossene Runs sind eingefroren.</p>
        </div>
        <button onClick={() => setShowNew(true)} disabled={!projectId || buildableVersions.length === 0} data-testid="builds-new-button" className="inline-flex items-center gap-2 px-4 py-2 text-sm text-white rounded-sm disabled:opacity-50" style={{ background: "#183153" }}>
          <Plus className="w-4 h-4" /> Neuer Build
        </button>
      </div>

      <div className="mb-4 flex items-center gap-3">
        <select value={projectId} onChange={(e) => setProjectId(e.target.value)} data-testid="builds-project-select" className="px-3 py-2 border border-neutral-300 rounded-sm text-sm">
          <option value="">Projekt wählen…</option>
          {projects.map((p) => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
        </select>
        {projectId && buildableVersions.length === 0 && (
          <span className="text-xs text-amber-700">Mindestens eine Prompt-Set-Version muss <strong>abgeschlossen</strong> sein.</span>
        )}
      </div>

      {projectId && (
        <div className="space-y-3">
          {builds.length === 0 && <div className="bg-white border border-neutral-200 px-5 py-10 text-center text-sm text-neutral-500">Noch keine Builds.</div>}
          {builds.map((b) => {
            const fset = findings.filter((f) => f.run_id === b.run_id);
            const frozen = b.status === "abgeschlossen" || b.status === "abgelehnt";
            return (
              <div key={b.run_id} className="bg-white border border-neutral-200" data-testid={`build-${b.run_id}`}>
                <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between flex-wrap gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-neutral-900 flex items-center gap-2">
                      {frozen && <Lock className="w-3.5 h-3.5 text-neutral-500" />} {b.name}
                    </div>
                    <div className="font-mono text-[10px] text-neutral-500">{b.run_id} · gestartet {fmtDate(b.started_at)} · beendet {fmtDate(b.finished_at)} · Owner: {userName(b.owner_id)}</div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <StatusPill kind="status" value={b.status} />
                    {!frozen && (
                      <select value={b.status} onChange={(e) => setStatus(b.run_id, e.target.value)} className="px-2 py-1 text-xs border border-neutral-300 rounded-sm">
                        {RUN_STATUS.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                      </select>
                    )}
                    {!frozen && <button onClick={() => addFinding(b.run_id)} data-testid={`build-add-finding-${b.run_id}`} className="text-xs px-2 py-1 border border-neutral-300 rounded-sm hover:bg-neutral-50">+ Finding</button>}
                    {!frozen && <button onClick={() => addArtifact(b)} className="text-xs px-2 py-1 border border-neutral-300 rounded-sm hover:bg-neutral-50">+ Artefakt</button>}
                  </div>
                </div>

                {/* Bezugskette */}
                <div className="px-5 py-2 bg-neutral-50 border-b border-neutral-100 font-mono text-[11px] text-neutral-700 flex flex-wrap gap-1 items-center">
                  <span className="text-neutral-400 uppercase tracking-widest text-[9px]">Audit-Pfad:</span>
                  {b.discovery_pack_id ? <span>Discovery v{b.discovery_pack_version}</span> : <span className="text-neutral-400">— ohne Discovery —</span>}
                  <span className="text-neutral-400">→</span>
                  <span>{b.prompt_set_name || b.prompt_set_id} v{b.prompt_version}</span>
                  <span className="text-neutral-400">→</span>
                  <span className="text-neutral-900">Build {b.run_id}</span>
                </div>

                <div className="px-5 py-3 grid md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Zweck / Kontext</div>
                    <div className="text-neutral-700 whitespace-pre-wrap">{b.purpose || "—"}</div>
                  </div>
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Notizen</div>
                    <div className="text-neutral-700 whitespace-pre-wrap">{b.notes || "—"}</div>
                  </div>
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Artefakte</div>
                    {(b.artifacts || []).length === 0 ? (
                      <div className="text-neutral-500 text-xs">{b.no_artifact_reason || "—"}</div>
                    ) : (
                      <ul className="space-y-1 text-xs">
                        {b.artifacts.map((a, i) => (
                          <li key={i} className="flex items-center gap-2">
                            <span className="font-mono text-[10px] text-neutral-500">[{a.type}]</span>
                            <a href={a.url} target="_blank" rel="noreferrer" className="text-[#183153] hover:underline truncate">{a.url}</a>
                            {!frozen && <button onClick={() => removeArtifact(b, i)} className="text-neutral-400 hover:text-red-600"><X className="w-3 h-3" /></button>}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>

                <div className="px-5 py-3 border-t border-neutral-100">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Übergabe an Review</div>
                  {frozen ? (
                    <div className="text-sm text-neutral-700 whitespace-pre-wrap">{b.handover_to_review || "—"}</div>
                  ) : (
                    <textarea
                      defaultValue={b.handover_to_review || ""}
                      onBlur={(e) => updateField(b.run_id, "handover_to_review", e.target.value)}
                      rows={2}
                      placeholder="Was soll Review prüfen?"
                      className="w-full px-3 py-2 text-sm border border-neutral-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-[#183153]"
                    />
                  )}
                </div>

                {fset.length > 0 && (
                  <div className="px-5 py-3 border-t border-neutral-100">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Findings ({fset.length})</div>
                    <div className="space-y-1">
                      {fset.map((f) => (
                        <div key={f.finding_id} className="flex items-center gap-2 text-xs">
                          <StatusPill kind="severity" value={f.severity} sharp />
                          <span className="truncate flex-1">{f.title}</span>
                          <StatusPill kind="status" value={f.status} sharp />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showNew && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowNew(false)}>
          <div className="bg-white border border-neutral-200 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-neutral-200 font-display-medium">Neuer Build Run</div>
            <form onSubmit={onCreate} className="p-5 space-y-3">
              <input required placeholder="Build-Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="build-name-input" className="w-full px-3 py-2 border border-neutral-300 rounded-sm text-sm" />
              <div>
                <label className="text-[11px] font-mono uppercase tracking-widest text-neutral-500">Prompt-Set-Version <span className="text-red-500">*</span></label>
                <select required value={form.prompt_set_id && form.prompt_version !== "" ? `${form.prompt_set_id}::${form.prompt_version}` : ""} onChange={(e) => {
                  const [sid, v] = (e.target.value || "::").split("::");
                  setForm({ ...form, prompt_set_id: sid, prompt_version: v });
                }} data-testid="build-set-select" className="w-full mt-1 px-3 py-2 border border-neutral-300 rounded-sm text-sm font-mono">
                  <option value="">— wählen —</option>
                  {buildableVersions.map((v) => (
                    <option key={`${v.set_id}::${v.version}`} value={`${v.set_id}::${v.version}`}>
                      {v.set_name} v{v.version} {v.discovery != null && `· Discovery v${v.discovery}`}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[11px] font-mono uppercase tracking-widest text-neutral-500">Owner <span className="text-red-500">*</span></label>
                <select required value={form.owner_id} onChange={(e) => setForm({ ...form, owner_id: e.target.value })} data-testid="build-owner-select" className="w-full mt-1 px-3 py-2 border border-neutral-300 rounded-sm text-sm">
                  <option value="">— wählen —</option>
                  {members.map((m) => <option key={m.user_id} value={m.user_id}>{m.name || m.user_id}</option>)}
                </select>
              </div>
              <textarea rows={2} placeholder="Zweck / Kontext" value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} className="w-full px-3 py-2 border border-neutral-300 rounded-sm text-sm" />
              <textarea rows={2} placeholder="Notizen (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full px-3 py-2 border border-neutral-300 rounded-sm text-sm" />
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowNew(false)} className="px-4 py-2 text-sm border border-neutral-300 rounded-sm hover:bg-neutral-50">Abbrechen</button>
                <button type="submit" data-testid="build-submit-button" className="px-4 py-2 text-sm text-white rounded-sm" style={{ background: "#183153" }}>Anlegen</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </InternalLayout>
  );
}
